import { Box, FormHelperText, OutlinedInput, Popover, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrandColours } from "./brand-colours";
import { EyeDropper } from "./eye-dropper";
import { clamp, transformColor } from "./utils";

import type { usePopover } from "../../../../utils/hooks/use-popover";
import "./colour-picker.css";

import type { BrandKitColor } from "../../../brand-kit/server/brand-kit-service";
import type { FC } from "react";

/** --- FROM META -------------------------------------------------------- */

let skipAddingToHistoryStack = false;

const WIDTH = 214;
const HEIGHT = 150;

interface Position {
  x: number;
  y: number;
}

const HEX_FULL_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const TRANSPARENT_RE = /^t(r(a(n(s(p(a(r(e(n(t)?)?)?)?)?)?)?)?)?)?$/i;

export const ColourPickerV2: FC<{
  popover: ReturnType<typeof usePopover<HTMLElement>>;
  onChange: (color: string, skipHistory: boolean) => void;
  colour: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}> = ({ popover, onChange, colour, inputRef }) => {
  const initial =
    colour === "transparent" ? transformColor("hex", "#000000") : transformColor("hex", colour);

  const [selfColor, setSelfColor] = useState(initial);
  const [editingValue, setEditingValue] = useState(
    colour === "transparent" ? "transparent" : colour.replace(/^#/, ""),
  );

  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
    setError(null);
  }, []);

  const handlePickerChange = useCallback(
    (hex: string) => {
      const newColor = transformColor("hex", hex);
      skipAddingToHistoryStack = true;

      setSelfColor(newColor);
      setEditingValue(newColor.hex.replace(/^#/, ""));

      onChange(newColor.hex, true);
    },
    [onChange],
  );

  const handleBrandClick = useCallback(
    (color: BrandKitColor) => {
      const newColor = transformColor("hex", color.hex);
      setSelfColor(newColor);
      setEditingValue(newColor.hex.replace(/^#/, ""));
      onChange(newColor.hex, false);
    },
    [onChange],
  );

  const saturationPos = useMemo(
    () => ({
      x: (selfColor.hsv.s / 100) * WIDTH,
      y: ((100 - selfColor.hsv.v) / 100) * HEIGHT,
    }),
    [selfColor.hsv],
  );

  const onMoveSaturation = (pos: Position): void => {
    const newHsv = {
      ...selfColor.hsv,
      s: (pos.x / WIDTH) * 100,
      v: 100 - (pos.y / HEIGHT) * 100,
    };

    const newColor = transformColor("hsv", newHsv);
    setSelfColor(newColor);
    setEditingValue(newColor.hex.replace(/^#/, ""));
  };

  const huePos = useMemo(() => ({ x: (selfColor.hsv.h / 360) * WIDTH }), [selfColor.hsv.h]);

  const onMoveHue = (pos: Position): void => {
    const newHsv = { ...selfColor.hsv, h: (pos.x / WIDTH) * 360 };
    const newColor = transformColor("hsv", newHsv);

    setSelfColor(newColor);
    setEditingValue(newColor.hex.replace(/^#/, ""));
  };

  /** ----- Push to parent when value changes (skipHistory aware) ----- */
  useEffect(() => {
    if (ref.current) {
      onChange(selfColor.hex, skipAddingToHistoryStack);
    }
  }, [selfColor.hex, onChange]);

  /** ----- closing popover (validation) ----- */
  const handleClose = useCallback(() => {
    const trimmed = editingValue.trim().toLowerCase();

    const candidate = trimmed === "transparent" ? "transparent" : `#${editingValue.trim()}`;

    if (trimmed === "transparent" || HEX_FULL_RE.test(candidate)) {
      onChange(candidate, false);
      setError(null);
      popover.handleClose();
    } else {
      setError("Invalid hex value. Must be 3 or 6 hex digits or 'transparent'.");
    }
  }, [editingValue, onChange, popover]);

  return (
    <Popover anchorEl={popover.anchorRef.current} open={popover.open} onClose={handleClose}>
      <Stack direction="row" spacing={2} padding={2} ref={ref} alignItems="center">
        <Box>
          <MoveWrapper
            className="color-picker-saturation"
            style={{
              width: WIDTH,
              height: HEIGHT,
              backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
              position: "relative",
            }}
            onChange={onMoveSaturation}
          >
            <div
              className="cursor"
              style={{
                position: "absolute",
                width: 12,
                height: 12,
                borderRadius: "50%",
                border: "2px solid white",
                background: selfColor.hex,
                left: saturationPos.x - 6,
                top: saturationPos.y - 6,
              }}
            />
          </MoveWrapper>

          <MoveWrapper
            className="color-picker-hue"
            style={{
              width: WIDTH,
              height: 14,
              marginTop: 8,
              background: "linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)",
              position: "relative",
            }}
            onChange={onMoveHue}
          >
            <div
              style={{
                position: "absolute",
                width: 12,
                height: 12,
                borderRadius: "50%",
                border: "2px solid white",
                backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
                left: huePos.x - 6,
                top: 1,
              }}
            />
          </MoveWrapper>
        </Box>

        <Stack gap={3}>
          <Box display="flex" justifyContent="center" gap={1}>
            <EyeDropper onChange={handlePickerChange} />

            <OutlinedInput
              inputProps={{ "aria-label": "hex" }}
              value={editingValue}
              startAdornment={TRANSPARENT_RE.exec(editingValue) ? null : "#"}
              onChange={handleInputChange}
              error={Boolean(error)}
              inputRef={inputRef}
              size="small"
              fullWidth
            />
          </Box>

          {error && <FormHelperText error>{error}</FormHelperText>}

          <BrandColours onClick={handleBrandClick} />
        </Stack>
      </Stack>
    </Popover>
  );
};

const MoveWrapper: FC<{
  className: string;
  style: React.CSSProperties;
  children: React.ReactNode;
  onChange: (p: Position) => void;
}> = ({ className, style, children, onChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragged = useRef(false);

  const move = (e: MouseEvent | React.MouseEvent): void => {
    const div = ref.current;
    if (!div) return;

    const rect = div.getBoundingClientRect();
    const x = clamp(e.clientX - rect.left, rect.width, 0);
    const y = clamp(e.clientY - rect.top, rect.height, 0);

    onChange({ x, y });
  };

  const onMouseDown = (e: React.MouseEvent): void => {
    if (e.button !== 0) return;

    move(e);

    const onMove = (ev: MouseEvent): void => {
      dragged.current = true;
      skipAddingToHistoryStack = true;
      move(ev);
    };

    const onUp = (ev: MouseEvent): void => {
      if (dragged.current) skipAddingToHistoryStack = false;

      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      move(ev);
      dragged.current = false;
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div ref={ref} className={className} style={style} onMouseDown={onMouseDown}>
      {children}
    </div>
  );
};
